// Router/provider settings shared by the full app (index.html, served at /lite) and
// Qlipper Lite (lite.html). Both pages live on the same origin, so they share
// localStorage: a router configured on one page is already filled in on the other.
//
// Pure ESM, no React. `storage` is injectable so Node tests can pass a stub.
import { getPreset } from './registry.js';

// Per-preset { baseUrl, apiKey, model } map. Same key Lite.jsx has used since the
// router merge (34194b8), so existing Lite users keep their settings.
export const LLM_CFG_KEY = 'lite_llm_cfg';
// Which preset the FULL app's "Router / API lain" tile uses. Separate from Lite's
// lite_analyze_provider on purpose: Lite defaults to the zero-key OpenRouter path,
// the full app's default stays Gemini until the user picks the router tile.
export const FULL_APP_PROVIDER_KEY = 'qweb_router_provider';
export const FULL_APP_DEFAULT_PROVIDER = 'omniroute';

function store(storage) {
  return storage || (typeof globalThis !== 'undefined' ? globalThis.localStorage : undefined);
}

export function loadLlmCfg(storage) {
  try {
    const raw = store(storage)?.getItem(LLM_CFG_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function saveLlmCfg(cfg, storage) {
  try { store(storage)?.setItem(LLM_CFG_KEY, JSON.stringify(cfg || {})); } catch { /* ignore */ }
}

export function loadFullAppProvider(storage) {
  try {
    const id = store(storage)?.getItem(FULL_APP_PROVIDER_KEY);
    return id && id !== 'gemini' && (getPreset(id) || id === 'custom') ? id : FULL_APP_DEFAULT_PROVIDER;
  } catch {
    return FULL_APP_DEFAULT_PROVIDER;
  }
}

export function saveFullAppProvider(id, storage) {
  try { store(storage)?.setItem(FULL_APP_PROVIDER_KEY, id); } catch { /* ignore */ }
}

// Merge a preset's defaults with the user's saved values → the {presetId, baseUrl,
// apiKey, model, requiresKey} shape chatCompletion/analyzeViaLlm take. Mirrors
// Lite.jsx's getLlm(); `openrouterModel` lets a caller pin its own OpenRouter default.
export function resolveLlm(presetId, cfgMap = {}, { openrouterModel } = {}) {
  if (!presetId || presetId === 'gemini') return null;
  const preset = getPreset(presetId);
  if (!preset) return null;
  const cfg = (cfgMap && cfgMap[presetId]) || {};
  return {
    presetId,
    baseUrl: cfg.baseUrl || preset.baseUrl,
    apiKey: cfg.apiKey || '',
    model: cfg.model || (presetId === 'openrouter' && openrouterModel ? openrouterModel : preset.defaultModels.cheap),
    requiresKey: cfg.requiresKey !== undefined ? cfg.requiresKey : preset.requiresKey,
  };
}

// '' when the resolved config can run an analysis, else a short Indonesian reason.
// The full app has no server-key fallback: a preset that requires a key needs one.
export function llmMissing(llm) {
  if (!llm) return 'Pilih router atau provider dulu.';
  if (!String(llm.baseUrl || '').trim()) return 'Base URL kosong.';
  if (llm.requiresKey && !String(llm.apiKey || '').trim()) return 'API key router belum diisi.';
  if (!String(llm.model || '').trim()) return 'Model belum dipilih.';
  return '';
}
